/**
 * Настройки автопредложений: порог отбора и суточные лимиты.
 *
 * Зачем это на витрине. Каждое предложение кандидату из поиска площадки
 * открывает его резюме, а открытие резюме списывает контакт из оплаченного
 * пакета — вернуть его нельзя. 26.09.2026 агент за четыре часа отправил 298
 * автопредложений, и из 1030 контактов hh осталось 414. Раньше эти числа
 * жили в переменных окружения и менялись только передеплоем; теперь их
 * правит администратор — реакция на перерасход занимает минуту, а не день.
 *
 * Порог отбора здесь не про экономию, а про отдачу: по данным того же дня
 * кандидаты с баллом 85+ дали 12 из 17 ответов «Подходит» на 210 приглашений,
 * а те, у кого ниже 60, — один ответ на 101 приглашение. Слабые кандидаты
 * почти не отвечают, но контакт тратят наравне с сильными.
 */
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  useGetKadryAutoofferSettingsQuery,
  useSetKadryAutoofferSettingsMutation,
} from '@shared/api';
import { kadryDateTime, useKadryAccess } from '@entities/kadry';
import { KadryBannerGap, KadryErrorBanner, KadryMuted, KadryPerson } from '../ui';

const Intro = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Block = styled(Card)`
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const BlockTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-size: 14px;
  font-weight: 700;
`;

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 13px;
  min-width: 190px;
`;

const NumberInput = styled.input`
  width: 110px;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-variant-numeric: tabular-nums;

  &:disabled {
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`;

const Switch = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;

  input {
    margin-top: 3px;
    flex: none;
  }
`;

const SwitchText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const Forecast = styled(KadryMuted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

/** Целое из поля ввода: пустое поле и мусор не должны превращаться в NaN. */
function toInt(raw: string, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export function Autooffer() {
  const { data, isLoading, error, refetch } = useGetKadryAutoofferSettingsQuery();
  const editable = useKadryAccess().canWrite('admin_settings');
  const [save, saveState] = useSetKadryAutoofferSettingsMutation();

  const [minScore, setMinScore] = useState(80);
  const [perRequest, setPerRequest] = useState(20);
  const [total, setTotal] = useState(60);
  const [mass, setMass] = useState(false);
  const [massPerRequest, setMassPerRequest] = useState(50);

  // Поля заполняются тем, что пришло с сервера, и обновляются после
  // сохранения (version растёт — иначе следующая правка получит 409).
  useEffect(() => {
    if (!data) return;
    setMinScore(data.min_score);
    setPerRequest(data.daily_limit_request);
    setTotal(data.daily_limit_total);
    setMass(data.mass_hiring);
    setMassPerRequest(data.mass_daily_limit_request);
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <SkeletonLine $width="40%" />
        <SkeletonLine />
        <SkeletonLine $width="60%" />
      </Card>
    );
  }
  if (error || !data) {
    return (
      <KadryErrorBanner
        error={error}
        onRefresh={refetch}
        title="Не удалось загрузить настройки предложений"
      />
    );
  }

  const dirty =
    minScore !== data.min_score ||
    perRequest !== data.daily_limit_request ||
    total !== data.daily_limit_total ||
    mass !== data.mass_hiring ||
    massPerRequest !== data.mass_daily_limit_request;

  async function onSave() {
    if (!data) return;
    try {
      await save({
        min_score: minScore,
        daily_limit_request: perRequest,
        daily_limit_total: total,
        mass_hiring: mass,
        mass_daily_limit_request: massPerRequest,
        version: data.version,
      }).unwrap();
    } catch {
      // Ошибка показывается баннером ниже.
    }
  }

  return (
    <>
      <Intro>
        <KadryMuted>
          Предложение кандидату из поиска открывает его резюме на площадке, а это списывает контакт
          из оплаченного пакета — вернуть его нельзя. Порог отсекает тех, кто почти не отвечает,
          лимиты не дают израсходовать пакет за один день. Ручные предложения кадровика лимитами не
          ограничиваются.
        </KadryMuted>
      </Intro>

      {saveState.error ? (
        <KadryBannerGap>
          <KadryErrorBanner
            error={saveState.error}
            onRefresh={refetch}
            onDismiss={() => saveState.reset()}
            title="Не удалось сохранить настройки"
          />
        </KadryBannerGap>
      ) : null}

      <Block>
        <BlockTitle>Порог отбора</BlockTitle>
        <KadryMuted>
          Балл, начиная с которого предложение уходит без кадровика. Кандидаты ниже порога остаются
          в реестре — их приглашает кадровик вручную, если сочтёт нужным.
        </KadryMuted>
        <Fields>
          <Field>
            Минимальный балл
            <NumberInput
              type="number"
              min={0}
              max={100}
              value={minScore}
              disabled={!editable}
              onChange={(e) => setMinScore(toInt(e.target.value, minScore))}
            />
          </Field>
        </Fields>
      </Block>

      <Block>
        <BlockTitle>Суточные лимиты</BlockTitle>
        <Switch>
          <input
            type="checkbox"
            checked={mass}
            disabled={!editable}
            onChange={(e) => setMass(e.target.checked)}
          />
          <SwitchText>
            <span>Массовый набор</span>
            <KadryMuted>
              Лимит считается по каждой вакансии отдельно, общий потолок на компанию не применяется.
              Режим для потока — вахта, линейный персонал. Расход контактов при нём выше.
            </KadryMuted>
          </SwitchText>
        </Switch>

        <Fields>
          {mass ? (
            <Field>
              На вакансию в сутки
              <NumberInput
                type="number"
                min={0}
                max={10000}
                value={massPerRequest}
                disabled={!editable}
                onChange={(e) => setMassPerRequest(toInt(e.target.value, massPerRequest))}
              />
            </Field>
          ) : (
            <>
              <Field>
                На заявку в сутки
                <NumberInput
                  type="number"
                  min={0}
                  max={10000}
                  value={perRequest}
                  disabled={!editable}
                  onChange={(e) => setPerRequest(toInt(e.target.value, perRequest))}
                />
              </Field>
              <Field>
                Всего в сутки
                <NumberInput
                  type="number"
                  min={0}
                  max={10000}
                  value={total}
                  disabled={!editable}
                  onChange={(e) => setTotal(toInt(e.target.value, total))}
                />
              </Field>
            </>
          )}
        </Fields>
        <Forecast>
          {mass
            ? `При массовом наборе каждая активная вакансия может отправить до ${massPerRequest} предложений в сутки — столько же контактов спишется с пакета.`
            : `Расход не превысит ${total} контактов в сутки по всем заявкам.`}
          {' Ноль в поле полностью останавливает автопредложения.'}
        </Forecast>
      </Block>

      <Actions>
        {editable ? (
          <Button onClick={onSave} disabled={!dirty || saveState.isLoading}>
            {saveState.isLoading ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        ) : (
          <KadryMuted>Только просмотр: изменение настроек доступно администратору кадров.</KadryMuted>
        )}
        <KadryMuted>
          Изменено {kadryDateTime(data.updated_at)}
          {data.updated_by ? ' · ' : ''}
          {data.updated_by ? <KadryPerson id={data.updated_by} /> : null}
        </KadryMuted>
      </Actions>
    </>
  );
}
